import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Image, Loader2, Download, Lock, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VisionBoard = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [boardImage, setBoardImage] = useState(null);
  const [lifestyleKeywords, setLifestyleKeywords] = useState('');
  const [aestheticKeywords, setAestheticKeywords] = useState('');

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  // During beta (SHOW_PRICING=false), all users have access
  const hasAccess = !SHOW_PRICING || tier === 'premium' || tier === 'pro' || tier === 'experience' || isBetaUser;

  const handleGenerate = async () => {
    if (!hasAccess && SHOW_PRICING) {
      toast.error('Upgrade to Premium to access Vision Board Generator');
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/vision-board/generate`,
        {
          lifestyle_keywords: lifestyleKeywords.split(',').map(k => k.trim()).filter(Boolean),
          aesthetic_keywords: aestheticKeywords.split(',').map(k => k.trim()).filter(Boolean)
        },
        { headers }
      );
      
      if (response.data.image_base64) {
        setBoardImage(response.data.image_base64);
        toast.success('Vision board created!');
      } else {
        toast.info('Vision board created, but image generation is still processing.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate vision board');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!boardImage) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${boardImage}`;
    link.download = 'vision-board.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // During beta, show coming soon message instead of paywall
  if (!hasAccess) {
    return (
      <DashboardLayout title="Vision Board Generator">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <Sparkles className="text-[#D4AF37] mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Bientôt disponible</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Cette fonctionnalité sera bientôt accessible. Continue ton parcours pour débloquer de nouvelles possibilités.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold px-8 py-4"
              data-testid="back-dashboard-btn"
            >
              Retour au parcours
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Vision Board Generator">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card-premium p-8">
              <div className="mb-6">
                <Image className="text-[#E5C585] mb-4" size={28} />
                <h2 className="font-serif text-2xl text-white mb-2">Create Your Vision Board</h2>
                <p className="text-[#A1A1AA]">
                  Generate a visual direction board with lifestyle and aesthetic inspiration.
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">Lifestyle Keywords</Label>
                  <Input
                    value={lifestyleKeywords}
                    onChange={(e) => setLifestyleKeywords(e.target.value)}
                    placeholder="luxury, travel, wellness, success"
                    className="input-underline"
                    data-testid="lifestyle-keywords-input"
                  />
                  <p className="text-[#52525B] text-xs mt-2">Separate keywords with commas</p>
                </div>

                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">Aesthetic Keywords</Label>
                  <Input
                    value={aestheticKeywords}
                    onChange={(e) => setAestheticKeywords(e.target.value)}
                    placeholder="minimal, elegant, warm, modern"
                    className="input-underline"
                    data-testid="aesthetic-keywords-input"
                  />
                  <p className="text-[#52525B] text-xs mt-2">Separate keywords with commas</p>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !lifestyleKeywords || !aestheticKeywords}
                  className="w-full btn-gold py-6 flex items-center justify-center gap-2"
                  data-testid="generate-board-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Generating... (this may take a minute)
                    </>
                  ) : (
                    <>
                      <Image size={18} />
                      Generate Vision Board
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {boardImage ? (
              <div className="card-premium p-4">
                <img 
                  src={`data:image/png;base64,${boardImage}`}
                  alt="Vision Board"
                  className="w-full border border-[#27272A]"
                  data-testid="vision-board-image"
                />
                <Button
                  onClick={handleDownload}
                  className="w-full btn-outline-gold py-4 mt-4 flex items-center justify-center gap-2"
                  data-testid="download-board-btn"
                >
                  <Download size={18} />
                  Download Vision Board
                </Button>
              </div>
            ) : (
              <div className="card-premium p-12 text-center h-full flex flex-col items-center justify-center min-h-[400px]">
                <Image className="text-[#27272A] mb-6" size={80} />
                <h3 className="font-serif text-xl text-white mb-3">Your Vision Board</h3>
                <p className="text-[#A1A1AA] max-w-sm">
                  Enter your keywords and generate a beautiful vision board for your brand direction.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VisionBoard;
